import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { usePermissions } from '../context/PermissionContext';
import StatusBadge from '../components/StatusBadge';

const STATUS_FILTERS = ['all', 'draft', 'submitted', 'approved', 'rejected', 'paid', 'voided'];

const formatAmount = (value) => `$${Number(value || 0).toFixed(2)}`;

const Expenses = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const { canView, hasPermission, loading: permissionsLoading } = usePermissions();

  const canCreate = hasPermission('expenses', 'create');

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      // scope=own keeps this page personal for manage-scope users too — the
      // all-reports view lives at /expenses/admin (Slice A1). List v2 returns
      // an envelope: { reports, total, limit, offset }.
      const response = await api.get('/expenses?scope=own');
      setReports(Array.isArray(response.data?.reports) ? response.data.reports : []);
    } catch (err) {
      toast.error('Failed to load expense reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    try {
      setCreating(true);
      const response = await api.post('/expenses', { title: newTitle.trim() });
      toast.success('Draft created');
      const newId = response.data?.id;
      if (newId) {
        navigate(`/expenses/${newId}`);
      } else {
        setShowCreateModal(false);
        fetchReports();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create report');
    } finally {
      setCreating(false);
    }
  };

  // While permissions resolve, show the loading state — never the fail-open flash.
  if (permissionsLoading) {
    return <div className="page-container"><div className="loading">Loading...</div></div>;
  }

  if (!canView('expenses')) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Expenses</h1></div>
        <div className="loading">You don't have access to expenses.</div>
      </div>
    );
  }

  const visibleReports = statusFilter === 'all'
    ? reports
    : reports.filter((r) => r.status === statusFilter);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Expenses</h1>
        {canCreate && (
          <button className="btn-primary" onClick={() => { setNewTitle(''); setShowCreateModal(true); }}>
            + New Report
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            className={statusFilter === s ? 'btn-primary btn-small' : 'btn-small'}
            onClick={() => setStatusFilter(s)}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">Loading expense reports...</div>
      ) : visibleReports.length === 0 ? (
        <div className="loading">
          {reports.length === 0 ? (
            <>
              <p>No expense reports yet.</p>
              {canCreate && (
                <button className="btn-primary" onClick={() => { setNewTitle(''); setShowCreateModal(true); }}>
                  Create your first expense report
                </button>
              )}
            </>
          ) : (
            <p>No {statusFilter} reports.</p>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Employee</th>
                <th>Status</th>
                <th>Total</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {visibleReports.map((report) => (
                <tr
                  key={report.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/expenses/${report.id}`)}
                >
                  <td>{report.title}</td>
                  <td>{report.employee_name || '-'}</td>
                  <td><StatusBadge status={report.status} /></td>
                  <td>{formatAmount(report.total_amount)}</td>
                  <td>{new Date(report.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New Expense Report</h2>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="e.g. August fuel and supplies"
                  autoFocus
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={creating || !newTitle.trim()}>
                  {creating ? 'Creating...' : 'Create Draft'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
